import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../hooks/redux';
import { useAdminProductCatalog } from './useAdminProductCatalog';
import { useAdminProductCreate } from './useAdminProductCreate';
import { useAdminProductFormState } from './useAdminProductFormState';
import { useCreateCardCollapse } from './useCreateCardCollapse';

export const useAdminProductFormController = () => {
  const { user } = useAppSelector((state) => state.user);
  const navigate = useNavigate();
  const state = useAdminProductFormState();
  const catalog = useAdminProductCatalog({
    products: state.products,
    searchTerm: state.searchTerm,
    activeSearchTerm: state.activeSearchTerm,
    draftStockById: state.draftStockById,
    draftPriceById: state.draftPriceById,
    loadingProducts: state.loadingProducts,
    loadingMoreProducts: state.loadingMoreProducts,
    currentProductsPage: state.currentProductsPage,
    productsHasMore: state.productsHasMore,
    productsTotal: state.productsTotal,
    productsSkip: state.productsSkip,
    savingStockId: state.savingStockId,
    savingPriceId: state.savingPriceId,
    loadingMaterialDetailsById: state.loadingMaterialDetailsById,
    setProducts: state.setProducts,
    setBaseProducts: state.setBaseProducts,
    setCurrentProductsPage: state.setCurrentProductsPage,
    setLoadingProducts: state.setLoadingProducts,
    setLoadingMoreProducts: state.setLoadingMoreProducts,
    setLoadingMaterialDetailsById: state.setLoadingMaterialDetailsById,
    setSavingStockId: state.setSavingStockId,
    setSavingPriceId: state.setSavingPriceId,
    setSavingSizesId: state.setSavingSizesId,
    setDeletingProductId: state.setDeletingProductId,
    setDraftStockById: state.setDraftStockById,
    setDraftPriceById: state.setDraftPriceById,
    setProductsSkip: state.setProductsSkip,
    setProductsHasMore: state.setProductsHasMore,
    setProductsTotal: state.setProductsTotal,
    setSearchTerm: state.setSearchTerm,
    setActiveSearchTerm: state.setActiveSearchTerm,
    setSearchingProducts: state.setSearchingProducts,
  });
  const create = useAdminProductCreate({
    form: state.form,
    sizes: state.sizes,
    materialTemplates: state.materialTemplates,
    setTypes: state.setTypes,
    setSizes: state.setSizes,
    setMaterialTemplates: state.setMaterialTemplates,
    setLoadingOptions: state.setLoadingOptions,
    setSubmitting: state.setSubmitting,
    fetchProducts: catalog.fetchProducts,
  });

  useCreateCardCollapse({
    createCardRef: state.createCardRef,
    isCreateOpen: state.isCreateOpen,
    setIsCreateOpen: state.setIsCreateOpen,
  });

  const { fetchOptions, handleCreateProduct } = create;
  const {
    fetchProducts,
    handleStockStep,
    handleDraftStockInput,
    handleDraftPriceInput,
    commitDraftStock,
    commitDraftPrice,
    handleMaterialSizesChange,
    handleAvailabilityChange,
    handleVariantAvailabilityChange,
    handleUploadMaterialImages,
    handleDeleteMaterialImage,
    handleDeleteProduct,
    handleProductPageChange,
    handleSearchProducts,
    handleClearSearch,
    handleLoadMaterialDetails,
  } = catalog;

  useEffect(() => {
    if (!user) {
      return;
    }
    if (!user.isAdmin) {
      navigate('/UserPage');
      return;
    }
    void fetchOptions();
    void fetchProducts({ append: false, page: 1 });
  }, [user, navigate, fetchOptions, fetchProducts]);

  return {
    user,
    form: state.form,
    types: state.types,
    materialTemplates: state.materialTemplates,
    sizes: state.sizes,
    filteredSizes: state.filteredSizes,
    selectedTypeRequiresSizes: state.selectedTypeRequiresSizes,
    loadingOptions: state.loadingOptions,
    submitting: state.submitting,
    products: state.products,
    loadingProducts: state.loadingProducts,
    loadingMoreProducts: state.loadingMoreProducts,
    savingStockId: state.savingStockId,
    savingPriceId: state.savingPriceId,
    savingSizesId: state.savingSizesId,
    loadingMaterialDetailsById: state.loadingMaterialDetailsById,
    deletingProductId: state.deletingProductId,
    isCreateOpen: state.isCreateOpen,
    draftStockById: state.draftStockById,
    draftPriceById: state.draftPriceById,
    currentProductsPage: state.currentProductsPage,
    productsHasMore: state.productsHasMore,
    productsTotal: state.productsTotal,
    searchTerm: state.searchTerm,
    activeSearchTerm: state.activeSearchTerm,
    searchingProducts: state.searchingProducts,
    createCardRef: state.createCardRef,
    setIsCreateOpen: state.setIsCreateOpen,
    setSearchTerm: state.setSearchTerm,
    handleCreateProduct,
    handleStockStep,
    handleDraftStockInput,
    handleDraftPriceInput,
    commitDraftStock,
    commitDraftPrice,
    handleMaterialSizesChange,
    handleAvailabilityChange,
    handleVariantAvailabilityChange,
    handleUploadMaterialImages,
    handleDeleteMaterialImage,
    handleDeleteProduct,
    handleProductPageChange,
    handleSearchProducts,
    handleClearSearch,
    handleLoadMaterialDetails,
  };
};
